// src/lib/hero-image-contract.ts

/**
 * Single source of truth for the homepage hero's responsive-image contract.
 *
 * The hero LCP image is delivered as a native `<picture>` (NOT `next/image`,
 * so color-scheme `<source>`s work) with two responsive candidates per variant:
 * a small DPR-1 candidate (`hero-{variant}-1136.webp`) whose width equals the
 * hero's measured desktop CSS render width, plus the full-res master
 * (`hero-{variant}.webp`, 2432×1365) kept as the DPR-2 candidate.
 *
 * This contract (width, `sizes`, and the `srcSet` builder) is consumed by:
 *   • `src/components/ui/HeroImage.ssr.tsx`            — the SSR `<picture>`
 *   • `src/components/shared/seo/HomeHeroPreload.tsx`  — the LCP preload hints
 *   • `src/components/ui/HeroImageThemeOverride.client.tsx` — manual-theme swap
 *   • `scripts/optimize-hero-assets.ts`                — the reproducible bake
 *
 * Keeping it in one place stops the (previously triplicated) width + `sizes`
 * string from drifting, which would silently break the `uses-responsive-images`
 * audit or double-fetch the LCP element. A Vitest parity test asserts the
 * preload's `imageSrcSet`/`imageSizes` equal the `<picture>`'s `srcSet`/`sizes`.
 */

/** Full-res master (DPR-2 candidate) intrinsic dimensions. */
export const HERO_MASTER_WIDTH = 2432
export const HERO_MASTER_HEIGHT = 1365

/**
 * DPR-1 candidate width — MUST equal the hero's measured desktop CSS render px.
 * Measured (Playwright, Lighthouse desktop viewport 1350×940, DPR 1): the hero
 * `<img>` is 1136 CSS px wide (the `w-[40rem] max-w-6xl` wrapper caps at
 * `max-w-6xl` = 1152px minus `px-2` = 16px). Height derives from the master
 * aspect: round(1136 × 1365 / 2432) = 638.
 */
export const HERO_CANDIDATE_WIDTH = 1136
export const HERO_CANDIDATE_HEIGHT = 638

/**
 * `sizes` mirroring the hero wrapper's layout (`w-[40rem] max-w-6xl sm:w-full
 * sm:px-2` inside `main.px-3`):
 *   • ≥1176px viewport → capped at max-w-6xl (1152px) minus px-2 (16px) = 1136px
 *   • ≥640px viewport  → 100vw minus main px-3 (24px) minus px-2 (16px) = 100vw-40px
 *   • <640px viewport  → fixed w-[40rem] (640px)
 * At the Lighthouse desktop viewport (1350px) this resolves to 1136px, so a
 * DPR-1 client picks the 1136w candidate and DPR-2 the 2432w master.
 */
export const HERO_SIZES =
  '(min-width: 1176px) 1136px, (min-width: 640px) calc(100vw - 40px), 640px'

/** Public asset base path (no extension) for a hero variant. */
export function heroBasePath(variant: 'light' | 'dark'): string {
  return `/images/hero-${variant}`
}

/**
 * The responsive `srcSet` for a hero variant as width descriptors: the small
 * DPR-1 candidate + the full master. Consumed by the `<picture>` `<source>`s,
 * the preload `imageSrcSet`, and the manual-theme-override enhancement so all
 * three select the identical candidate at a given DPR.
 */
export function buildHeroSrcSet(variant: 'light' | 'dark'): string {
  const base = heroBasePath(variant)
  return `${base}-${HERO_CANDIDATE_WIDTH}.webp ${HERO_CANDIDATE_WIDTH}w, ${base}.webp ${HERO_MASTER_WIDTH}w`
}
